(() => {
  const handleize = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w\u00C0-\u024f]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const BRAND_CANONICAL = { mini: 'mini-cooper' };

  const normalizeBrandKey = (value) => {
    const key = handleize(String(value || '').trim());
    return BRAND_CANONICAL[key] || key;
  };

  const pickerBrandLabel = (label, value) => {
    const key = normalizeBrandKey(value || label);
    if (key === 'mini-cooper') return 'Mini Cooper';
    const trimmed = String(label || '').trim();
    if (trimmed === 'Mini') return 'Mini Cooper';
    return trimmed;
  };

  const PRODUCT_LINE_TAGS = new Set([
    'seatarmour',
    'seat-towels',
    'center-console',
    'pet-beds',
    'seat-belt',
    'custom-corporate',
  ]);

  const parsePathTags = (pathname) => {
    const match = pathname.match(/\/collections\/all\/([^/?#]+)/i);
    if (!match) return [];
    return match[1]
      .split('+')
      .map((tag) => decodeURIComponent(tag).trim())
      .filter(Boolean);
  };

  const readBrandFromUrl = (lineTag) => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('sa_brand');
    if (fromQuery) return handleize(fromQuery);

    if (/\/search\/?$/i.test(window.location.pathname)) {
      const searchTerm = params.get('q');
      if (searchTerm) return handleize(searchTerm);
    }

    const lineHandle = handleize(lineTag);
    const fromPath = parsePathTags(window.location.pathname).find((tag) => {
      const handle = handleize(tag);
      return handle !== lineHandle && !PRODUCT_LINE_TAGS.has(handle);
    });
    if (fromPath) return handleize(fromPath);

    const fromFilterTag = params.getAll('filter.p.tag').find((tag) => handleize(tag) !== lineHandle);
    return fromFilterTag ? handleize(fromFilterTag) : '';
  };

  const readColorFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('sa_color') || params.get('filter.v.option.color') || '';
  };

  const readSizeFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return handleize(params.get('sa_size') || '');
  };

  const setSelectValue = (select, value) => {
    if (!select || !value) return false;
    const raw = String(value).trim();
    const normalized = handleize(raw);
    const option =
      [...select.options].find((entry) => entry.value === raw) ||
      [...select.options].find((entry) => handleize(entry.value) === normalized) ||
      [...select.options].find((entry) => handleize(entry.textContent) === normalized);
    if (!option) return false;
    select.value = option.value;
    return true;
  };

  const syncPickerActiveState = (picker) => {
    picker.querySelectorAll('.sa-picker__field').forEach((field) => {
      const select = field.querySelector('select');
      field.classList.toggle('is-active', Boolean(select?.value));
    });
  };

  const syncPickerFromUrl = (picker) => {
    const mode = picker.dataset.navigate || 'filter';
    const lineTag = picker.dataset.lineTag || '';
    const brandSelect = picker.querySelector('[data-sa-picker-brand]');
    const colorSelect = picker.querySelector('[data-sa-picker-color]');

    if (mode === 'product') {
      setSelectValue(colorSelect, readColorFromUrl());
      setSelectValue(picker.querySelector('[data-sa-picker-size]'), readSizeFromUrl());
      syncPickerActiveState(picker);
      return;
    }

    setSelectValue(brandSelect, readBrandFromUrl(lineTag));
    setSelectValue(colorSelect, readColorFromUrl());
    setSelectValue(picker.querySelector('[data-sa-picker-size]'), readSizeFromUrl());
    syncPickerActiveState(picker);
  };

  const normalizeLineTag = (tag) => {
    const handle = handleize(tag);
    if (handle === 'seat-armour') return 'seatarmour';
    return handle;
  };

  const readLineFromPath = () => {
    const match = window.location.pathname.match(/\/collections\/all\/([^/?#]+)/i);
    if (!match) return '';
    const tags = match[1]
      .split('+')
      .map((entry) => normalizeLineTag(decodeURIComponent(entry.trim())))
      .filter(Boolean);
    for (const tag of tags) {
      if (PRODUCT_LINE_TAGS.has(tag)) return tag;
    }
    return '';
  };

  const resolvePickerLine = (picker) => normalizeLineTag(picker.dataset.lineTag || '') || readLineFromPath();

  const copyPriceParams = (fromUrl, toUrl) => {
    fromUrl.searchParams.forEach((value, key) => {
      if (/^filter\.v\.price\./i.test(key) && value) {
        toUrl.searchParams.set(key, value);
      }
    });
  };

  const isPetBedsPath = (pathname) => /\/pet-beds(?:\/|$|\+)/i.test(pathname || '');

  const CATALOG_KEYS = ['sa_type', 'sa_line', 'sa_life', 'sa_group'];

  const copyCatalogParams = (fromUrl, toUrl) => {
    CATALOG_KEYS.forEach((key) => {
      const value = fromUrl.searchParams.get(key);
      if (value && !toUrl.searchParams.has(key)) toUrl.searchParams.set(key, value);
    });
  };

  const copyShopFilters = (fromUrl, toUrl) => {
    ['sa_brand', 'sa_color', 'sort_by'].forEach((key) => {
      const value = fromUrl.searchParams.get(key);
      if (value) toUrl.searchParams.set(key, value);
    });
    copyPriceParams(fromUrl, toUrl);
    if (isPetBedsPath(toUrl.pathname)) {
      const size = fromUrl.searchParams.get('sa_size');
      if (size) toUrl.searchParams.set('sa_size', size);
    } else {
      toUrl.searchParams.delete('sa_size');
    }
  };

  const buildLineFilterUrl = (picker) => {
    const base = (picker.dataset.baseUrl || '/collections/all').replace(/\/$/, '');
    const line = resolvePickerLine(picker);
    const color = picker.querySelector('[data-sa-picker-color]')?.value || '';
    const size = handleize(picker.querySelector('[data-sa-picker-size]')?.value || '');
    const current = new URL(window.location.href);

    let path = base;
    if (line) {
      path += `/${line}`;
    }

    const url = new URL(path, window.location.origin);
    ['sa_brand', 'sa_color', 'sa_size', 'filter.v.option.color'].forEach((key) => url.searchParams.delete(key));
    [...url.searchParams.keys()].forEach((key) => {
      if (key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')) {
        url.searchParams.delete(key);
      }
    });

    copyPriceParams(current, url);
    copyCatalogParams(current, url);

    const sortBy = current.searchParams.get('sort_by');
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    }

    if (color) {
      url.searchParams.set('sa_color', color);
    }

    if (size && isPetBedsPath(url.pathname)) {
      url.searchParams.set('sa_size', size);
    }

    return `${url.pathname}${url.search}`;
  };

  const buildPickerUrl = (picker) => {
    const mode = picker.dataset.navigate || 'filter';
    if (mode === 'product') {
      return buildLineFilterUrl(picker);
    }

    const base = (picker.dataset.baseUrl || '/collections/all').replace(/\/$/, '');
    const line = resolvePickerLine(picker);
    const brandSelect = picker.querySelector('[data-sa-picker-brand]');
    const colorSelect = picker.querySelector('[data-sa-picker-color]');
    const sizeSelect = picker.querySelector('[data-sa-picker-size]');
    const brand = handleize(brandSelect?.value || '');
    const color = colorSelect?.value || '';
    const size = handleize(sizeSelect?.value || '');

    let path = base;
    if (line) {
      path += `/${line}`;
    }

    const url = new URL(path, window.location.origin);
    const current = new URL(window.location.href);

    ['sa_brand', 'sa_color', 'sa_size', 'filter.v.option.color'].forEach((key) => url.searchParams.delete(key));
    [...url.searchParams.keys()].forEach((key) => {
      if (key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')) {
        url.searchParams.delete(key);
      }
    });

    copyPriceParams(current, url);
    copyCatalogParams(current, url);

    if (brand) {
      url.searchParams.set('sa_brand', brand);
    }

    const sortBy = current.searchParams.get('sort_by');
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    }

    if (color) {
      url.searchParams.set('sa_color', color);
    }

    if (size && isPetBedsPath(url.pathname)) {
      url.searchParams.set('sa_size', size);
    }

    return `${url.pathname}${url.search}`;
  };

  const navigatePicker = (picker, target) => {
    const mode = picker.dataset.navigate || 'filter';
    if (mode === 'product') {
      if (target?.matches('[data-sa-picker-color], [data-sa-picker-size]')) {
        const filterUrl = buildLineFilterUrl(picker);
        window.location.assign(filterUrl);
        return;
      }
      const select = picker.querySelector('[data-sa-picker-select]');
      if (select?.value) window.location.assign(select.value);
      return;
    }

    if (mode === 'stay') return;

    const filterTarget = buildPickerUrl(picker);
    if (!filterTarget) return;
    window.location.assign(filterTarget);
  };

  const comboOptionValue = (opt) => opt.getAttribute('data-value') ?? opt.dataset.value ?? '';

  const comboOptionMatches = (opt, value) => {
    const optVal = comboOptionValue(opt);
    if (optVal === value) return true;
    if (!value) return optVal === '';
    return normalizeBrandKey(optVal) === normalizeBrandKey(value);
  };

  const closeHomepageCombos = (except) => {
    document.querySelectorAll('[data-sa-picker] [data-sa-combo].is-open').forEach((combo) => {
      if (combo.closest('[data-sa-filterbar]') || combo === except) return;
      combo.classList.remove('is-open');
      combo.querySelector('[data-sa-combo-open]')?.setAttribute('aria-expanded', 'false');
    });
  };

  const syncHomepageCombo = (combo) => {
    const select = combo.querySelector('select');
    const strong = combo.querySelector('[data-sa-combo-value]');
    const em = combo.querySelector('.sa-finder__trigger em, .sa-combo__trigger em');
    if (!select) return;
    const value = select.value || '';
    const option = [...combo.querySelectorAll('[data-sa-combo-option]')].find((opt) =>
      comboOptionMatches(opt, value)
    );
    let label = option?.dataset.label || select.selectedOptions[0]?.textContent?.trim() || '';
    label = pickerBrandLabel(label, value);
    const hint = option?.dataset.hint || '';
    if (strong && label) strong.textContent = label;
    if (em && hint) em.textContent = hint;
    combo.classList.toggle('is-filled', Boolean(value));
    combo.querySelectorAll('[data-sa-combo-option]').forEach((opt) => {
      opt.classList.toggle('is-on', comboOptionMatches(opt, value));
    });
    const picker = combo.closest('[data-sa-picker]');
    if (!picker) return;
    picker.querySelectorAll('[data-sa-finder-chip]').forEach((chip) => {
      if ((chip.dataset.target || 'brand') !== 'brand') return;
      chip.classList.toggle('is-on', comboOptionMatches(chip, picker.querySelector('[data-sa-picker-brand]')?.value || ''));
    });
  };

  const initHomepageCombos = () => {
    document.querySelectorAll('[data-sa-picker] [data-sa-combo]').forEach((combo) => {
      if (combo.closest('[data-sa-filterbar]')) return;
      if (combo.dataset.saComboBound === 'true') return;
      combo.dataset.saComboBound = 'true';

      const trigger = combo.querySelector('[data-sa-combo-open]');
      const select = combo.querySelector('select');
      const search = combo.querySelector('[data-sa-combo-search]');
      const empty = combo.querySelector('[data-sa-combo-empty]');

      trigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const willOpen = !combo.classList.contains('is-open');
        closeHomepageCombos(combo);
        combo.classList.toggle('is-open', willOpen);
        trigger.setAttribute('aria-expanded', String(willOpen));
        if (willOpen) {
          search?.focus();
        }
      });

      combo.querySelector('[data-sa-combo-panel]')?.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      combo.querySelectorAll('[data-sa-combo-option]').forEach((opt) => {
        opt.addEventListener('click', () => {
          if (!select) return;
          select.value = comboOptionValue(opt);
          syncHomepageCombo(combo);
          closeHomepageCombos();
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      search?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
      });

      search?.addEventListener('input', () => {
        const q = search.value.trim().toLowerCase();
        let visible = 0;
        combo.querySelectorAll('[data-sa-combo-option]').forEach((opt) => {
          const hay = `${opt.dataset.label || ''} ${opt.textContent || ''}`.toLowerCase();
          const hide = Boolean(q) && !hay.includes(q);
          opt.hidden = hide;
          if (!hide) visible += 1;
        });
        if (empty) empty.hidden = visible > 0;
      });

      syncHomepageCombo(combo);
    });

    document.querySelectorAll('[data-sa-finder-chip]').forEach((chip) => {
      if (chip.dataset.saChipBound === 'true') return;
      chip.dataset.saChipBound = 'true';
      chip.addEventListener('click', () => {
        const picker = chip.closest('[data-sa-picker]');
        const target = chip.dataset.target || 'brand';
        const select = picker?.querySelector(`[data-sa-picker-${target}]`);
        if (!select) return;
        select.value = comboOptionValue(chip);
        const combo = select.closest('[data-sa-combo]');
        if (combo) syncHomepageCombo(combo);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const initPickers = () => {
    document.querySelectorAll('[data-sa-picker]').forEach((picker) => syncPickerFromUrl(picker));
    initHomepageCombos();
    document.querySelectorAll('[data-sa-picker] [data-sa-combo]').forEach((combo) => {
      if (!combo.closest('[data-sa-filterbar]')) syncHomepageCombo(combo);
    });
    document.dispatchEvent(new CustomEvent('sa:picker-synced'));
  };

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-sa-combo]')) return;
    closeHomepageCombos();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeHomepageCombos();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPickers);
  } else {
    initPickers();
  }
  document.addEventListener('shopify:section:load', initPickers);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-sa-cat-select]') && target.value) {
      const next = new URL(target.value, window.location.origin);
      copyShopFilters(new URL(window.location.href), next);
      window.location.assign(`${next.pathname}${next.search}`);
      return;
    }
    if (!target.matches('[data-sa-picker-select], [data-sa-picker-brand], [data-sa-picker-color], [data-sa-picker-size]')) return;
    const picker = target.closest('[data-sa-picker]') || document.querySelector('[data-sa-filterbar] [data-sa-picker]');
    if (!picker) return;
    syncPickerActiveState(picker);
    navigatePicker(picker, target);
  });
})();
